import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-cloud-arrow-up',
    title: '1. AWS Migration & Enterprise Modernization',
    desc: 'Execute seamless lift-and-shift, re-platforming, or complete refactoring of legacy workloads to AWS using proven Migration Acceleration Program (MAP) methodologies.',
  },
  {
    icon: 'fa-solid fa-brain',
    title: '2. Generative AI & AWS Bedrock Solutions',
    desc: 'Build secure, scalable generative AI applications and custom RAG systems using Amazon Bedrock, SageMaker, and foundational models tailored to your enterprise data.',
  },
  {
    icon: 'fa-solid fa-bolt',
    title: '3. Serverless Architecture & Microservices',
    desc: 'Design high-throughput, event-driven architectures utilizing AWS Lambda, API Gateway, Amazon DynamoDB, and Step Functions to eliminate server management overhead.',
  },
  {
    icon: 'fa-solid fa-dharmachakra',
    title: '4. Cloud-Native Kubernetes (Amazon EKS & ECS)',
    desc: 'Deploy, scale, and manage containerized microservices pipelines across multi-AZ environments with Amazon EKS, ECS, and AWS Fargate.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '5. Modern Data Lakes & Analytics',
    desc: 'Consolidate structured and unstructured data using Amazon S3, AWS Glue, Redshift, and Athena to power real-time business intelligence and machine learning.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '6. AWS Security, Identity & Compliance',
    desc: 'Enforce zero-trust architecture, automated threat detection, and continuous compliance using AWS IAM, GuardDuty, Security Hub, and AWS KMS.',
  },
  {
    icon: 'fa-solid fa-infinity',
    title: '7. DevOps & Infrastructure as Code (IaC)',
    desc: 'Automate software delivery pipelines and cloud provisioning using Terraform, AWS CDK, CloudFormation, and AWS CodePipeline.',
  },
  {
    icon: 'fa-solid fa-chart-line-down',
    title: '8. AWS FinOps & Cost Optimization',
    desc: 'Identify cost-saving opportunities, eliminate orphaned resources, implement Savings Plans/Reserved Instances, and establish continuous AWS Cost Explorer governance.',
  },
  {
    icon: 'fa-solid fa-network-wired',
    title: '9. Hybrid Cloud & Networking Solutions',
    desc: 'Connect on-premises data centers to AWS securely with AWS Direct Connect, Transit Gateway, VPC peering, and hybrid architectures using AWS Outposts.',
  },
  {
    icon: 'fa-solid fa-headset',
    title: '10. AWS Managed Services & Disaster Recovery',
    desc: 'Ensure 99.99% uptime with 24/7 proactive cloud monitoring, automated backup workflows via AWS Backup, and rapid disaster recovery setup (AWS Elastic Disaster Recovery).',
  },
];

const whyChooseUs = [
  'AWS Certified Solutions Architects and Cloud Security Specialists',
  'Deep expertise in Amazon Bedrock and AWS SageMaker AI rollouts',
  'Serverless-first approach to maximize performance and cut compute costs',
  'Adherence to the AWS Well-Architected Framework across 6 pillars',
  'Automated Infrastructure as Code (IaC) deployment using Terraform and CDK',
  'Proven data lakehouse engineering with Amazon Redshift and S3',
  'Proactive FinOps strategies yielding measurable cloud cost reductions',
  'End-to-end containerization expertise using EKS, ECS, and Fargate',
  'Multi-region high availability and fault-tolerant disaster recovery setups',
  '24/7 continuous cloud infrastructure monitoring and incident management',
];

const processSteps = [
  { step: '01', title: 'AWS Well-Architected Audit', desc: 'Assess existing architecture against AWS pillars to uncover security risks, cost inefficiencies, and performance bottlenecks.' },
  { step: '02', title: 'Migration & Cloud Strategy', desc: 'Define your cloud roadmap, design landing zones, establish network routing, and set up landing zone security.' },
  { step: '03', title: 'Automated IaC Provisioning', desc: 'Write re-usable Terraform or AWS CDK modules to provision compliant infrastructure in automated environments.' },
  { step: '04', title: 'Workload Migration & Refactoring', desc: 'Migrate databases, compute instances, and application containers with zero data loss and minimal downtime.' },
  { step: '05', title: 'Security & CI/CD Integration', desc: 'Embed automated security guardrails, vulnerability scanning, and CI/CD pipelines into the delivery workflow.' },
  { step: '06', title: 'Continuous FinOps & Management', desc: 'Track resource consumption, manage savings strategies, and deliver 24/7 proactive infrastructure management.' },
];

const techStack = [
  { category: 'Compute & Containers', items: ['AWS Lambda', 'Amazon EC2', 'Amazon EKS', 'Amazon ECS', 'AWS Fargate'] },
  { category: 'AI, ML & Analytics', items: ['Amazon Bedrock', 'Amazon SageMaker', 'Amazon Redshift', 'AWS Glue', 'Amazon Athena'] },
  { category: 'Database & Storage', items: ['Amazon S3', 'Amazon DynamoDB', 'Amazon Aurora', 'Amazon RDS', 'ElastiCache'] },
  { category: 'DevOps & Security', items: ['AWS CDK', 'Terraform', 'AWS IAM', 'AWS GuardDuty', 'AWS Security Hub'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const AWSCloudSolutions = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Technology Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              AWS Cloud <span className="gradient-text-cyan">Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we build resilient, scalable, and secure cloud environments powered by Amazon Web Services. From serverless microservices and cloud migrations to enterprise AI deployments on Amazon Bedrock, we accelerate your digital transformation.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Elevate Your AWS Architecture &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              AWS Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">AWS Technology Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">AWS Engineering Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Optimize & Scale Your Cloud with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Modernize legacy applications, reduce operational expenses, and build intelligent AWS cloud environments designed for infinite scale.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with an AWS Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AWSCloudSolutions;